import { ApolloError } from "@apollo/client";
import { Alert, Button, Dialog, DialogContent, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { GqlPermissions } from "~/__generated__";
import useHasTenantPermission from "~/modules/auth/hooks/useHasPermission";

import ObservationTypeFunctionTypeDataGrid from "../ObservationTypeFunctionTypeDataGrid";
import { ObservationTypeFunctionTypeDataGridFragment } from "../ObservationTypeFunctionTypeDataGrid/fragments.generated";
import ObservationTypeFunctionTypeForm, {
    ObservationTypeFunctionTypeFormInput,
} from "../ObservationTypeFunctionTypeForm";

type Props = {
    observationTypeFunctionTypes: ObservationTypeFunctionTypeDataGridFragment[];
    observationTypeId: string;
    researchTypeId: string;
    onAddFunctionType: (data: ObservationTypeFunctionTypeFormInput) => Promise<void>;
    isAddFunctionTypeLoading?: boolean;
    addFunctionTypeError?: ApolloError;
    onRemoveFunctionType: (ecologicalFunctionTypeId: string) => Promise<void>;
    isRemoveFunctionTypeLoading: boolean;
    removeFunctionTypeError?: ApolloError;
};

export default function ObservationTypeFunctionTypeListCard({
    observationTypeFunctionTypes,
    observationTypeId,
    researchTypeId,
    onAddFunctionType,
    isAddFunctionTypeLoading,
    addFunctionTypeError,
    onRemoveFunctionType,
    isRemoveFunctionTypeLoading,
    removeFunctionTypeError,
}: Props) {
    const { t } = useTranslation("observationType");
    const isAdmin = useHasTenantPermission(GqlPermissions.Administrator);
    const [showFunctionTypeDialog, setShowFunctionTypeDialog] = useState<boolean>(false);

    const handleAddFunctionType = async (data: ObservationTypeFunctionTypeFormInput) => {
        await onAddFunctionType(data);
        setShowFunctionTypeDialog(false);
    };

    return (
        <>
            {isAdmin && (
                <>
                    <Button onClick={() => setShowFunctionTypeDialog(true)}>
                        {t("observationTypeFunctionTypeListCard.actionAddFunctionType")}
                    </Button>
                    <Dialog open={showFunctionTypeDialog} onClose={() => setShowFunctionTypeDialog(false)}>
                        <DialogContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t("observationTypeFunctionTypeListCard.actionAddFunctionType")}
                            </Typography>
                            <ObservationTypeFunctionTypeForm
                                onSubmit={handleAddFunctionType}
                                isLoading={isAddFunctionTypeLoading}
                                error={addFunctionTypeError}
                                researchTypeId={researchTypeId}
                                observationTypeId={observationTypeId}
                            />
                        </DialogContent>
                    </Dialog>
                </>
            )}
            {removeFunctionTypeError && <Alert severity="error">{removeFunctionTypeError.message}</Alert>}
            <ObservationTypeFunctionTypeDataGrid
                observationTypeFunctionTypes={observationTypeFunctionTypes}
                isAdmin={isAdmin}
                onDeleteClick={ecologicalFunctionTypeId => onRemoveFunctionType(ecologicalFunctionTypeId)}
                isLoading={isRemoveFunctionTypeLoading}
            />
        </>
    );
}
